/**
 * @typedef {{id: number, title?: string, parentId?: number|null, children?: CategoryNode[]}} CategoryNode
 */

/**
 * Builds a nested category tree from a flat list linked by parentId.
 * @param {CategoryNode[]} flat
 * @returns {CategoryNode[]}
 */
export const buildCategoryTree = (flat = []) => {
    const categoryMap = new Map();
    const roots = [];

    for (const category of flat) {
        categoryMap.set(category.id, {...category, children: []});
    }

    for (const category of flat) {
        const node = categoryMap.get(category.id);
        if (!category.parentId) {
            roots.push(node);
            continue;
        }
        const parent = categoryMap.get(category.parentId);
        if (parent) {
            parent.children.push(node);
        } else {
            roots.push(node);
        }
    }

    return roots;
};

/**
 * Returns whether the category has no children in the flat list.
 * @param {number} id
 * @param {CategoryNode[]} flat
 * @returns {boolean}
 */
export const isLeafCategory = (id, flat = []) => {
    return !flat.some((category) => category.parentId === id);
};

/**
 * Builds a map of parentId → direct child ids.
 * @param {CategoryNode[]} flat
 * @returns {Map<number, number[]>}
 */
const buildChildrenByParentMap = (flat = []) => {
    const childrenByParent = new Map();
    for (const category of flat) {
        if (!category.parentId) {
            continue;
        }
        const siblings = childrenByParent.get(category.parentId) ?? [];
        siblings.push(category.id);
        childrenByParent.set(category.parentId, siblings);
    }
    return childrenByParent;
};

/**
 * Collects all descendant category ids under the given node (any depth, excluding self).
 * @param {number} id
 * @param {CategoryNode[]} flat
 * @returns {number[]}
 */
export const getDescendantCategoryIds = (id, flat = []) => {
    if (id == null || !flat.length) {
        return [];
    }

    const childrenByParent = buildChildrenByParentMap(flat);
    const descendantIds = [];
    const stack = [...(childrenByParent.get(id) ?? [])];
    while (stack.length) {
        const currentId = stack.pop();
        descendantIds.push(currentId);
        const children = childrenByParent.get(currentId);
        if (children?.length) {
            stack.push(...children);
        }
    }
    return descendantIds;
};

/**
 * Collects all descendant leaf category ids under the given node.
 * If the node is already a leaf, returns [id].
 * @param {number} id
 * @param {CategoryNode[]} flat
 * @returns {number[]}
 */
export const getDescendantLeafIds = (id, flat = []) => {
    if (id == null || !flat.length) {
        return [];
    }
    if (isLeafCategory(id, flat)) {
        return [id];
    }

    const childrenByParent = buildChildrenByParentMap(flat);
    const leafIds = [];
    const stack = [...(childrenByParent.get(id) ?? [])];
    while (stack.length) {
        const currentId = stack.pop();
        const children = childrenByParent.get(currentId);
        if (!children?.length) {
            leafIds.push(currentId);
            continue;
        }
        stack.push(...children);
    }
    return leafIds;
};

/**
 * Returns true when an ancestor of the category is already in the selected set.
 * @param {number} id
 * @param {Iterable<number>} selectedIds
 * @param {CategoryNode[]} flat
 * @returns {boolean}
 */
export const isCategoryCoveredByAncestor = (id, selectedIds = [], flat = []) => {
    const selectedSet = selectedIds instanceof Set ? selectedIds : new Set(selectedIds);
    if (!selectedSet.size) {
        return false;
    }
    const ancestors = getCategoryAncestorIds(id, flat);
    return ancestors.some((ancestorId) => ancestorId !== id && selectedSet.has(ancestorId));
};

/**
 * Adds a category to the selection while absorbing descendant selections into the parent.
 * Selecting a node already covered by an ancestor is a no-op.
 * @param {number[]} selectedIds
 * @param {number} categoryId
 * @param {CategoryNode[]} flat
 * @returns {number[]}
 */
export const absorbCategoryIntoSelection = (selectedIds = [], categoryId, flat = []) => {
    if (categoryId == null) {
        return selectedIds;
    }
    if (isCategoryCoveredByAncestor(categoryId, selectedIds, flat)) {
        return selectedIds;
    }

    const descendantIdSet = new Set(getDescendantCategoryIds(categoryId, flat));
    const withoutDescendants = selectedIds.filter(
        (id) => id !== categoryId && !descendantIdSet.has(id),
    );
    return [...withoutDescendants, categoryId];
};

/**
 * Expands a set of selected category ids (any depth) to unique leaf category ids.
 * @param {number[]} selectedIds
 * @param {CategoryNode[]} flat
 * @returns {number[]}
 */
export const resolveLeafCategoryIds = (selectedIds = [], flat = []) => {
    if (!selectedIds?.length || !flat?.length) {
        return [];
    }

    const leafIds = new Set();
    for (const id of selectedIds) {
        for (const leafId of getDescendantLeafIds(id, flat)) {
            leafIds.add(leafId);
        }
    }
    return Array.from(leafIds);
};

/**
 * Builds a cascading title path for a category (e.g. Root → Mid → Leaf).
 * @param {number|CategoryNode} categoryOrId
 * @param {CategoryNode[]} flat
 * @param {string} [separator=' → ']
 * @returns {string}
 */
export const getCascadingCategoryTitle = (categoryOrId, flat = [], separator = ' → ') => {
    if (categoryOrId == null || !flat.length) {
        return typeof categoryOrId === 'object' ? (categoryOrId?.title ?? '') : '';
    }

    const byId = new Map(flat.map((category) => [category.id, category]));
    let current = typeof categoryOrId === 'object'
        ? categoryOrId
        : byId.get(categoryOrId);

    if (!current) {
        return '';
    }

    const titles = [];
    while (current) {
        titles.unshift(current.title ?? '');
        current = current.parentId ? byId.get(current.parentId) : null;
    }
    return titles.filter(Boolean).join(separator);
};

/**
 * Returns ancestor ids from root to the given category (inclusive).
 * @param {number} id
 * @param {CategoryNode[]} flat
 * @returns {number[]}
 */
export const getCategoryAncestorIds = (id, flat = []) => {
    const byId = new Map(flat.map((category) => [category.id, category]));
    const ancestors = [];
    let current = byId.get(id);
    while (current) {
        ancestors.unshift(current.id);
        current = current.parentId ? byId.get(current.parentId) : null;
    }
    return ancestors;
};

/**
 * Filters a nested tree to nodes matching the query (case-insensitive) and their ancestors.
 * @param {CategoryNode[]} tree
 * @param {string} query
 * @returns {CategoryNode[]}
 */
export const filterCategoryTreeByQuery = (tree = [], query = '') => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) {
        return tree;
    }

    const filterNode = (node) => {
        const titleMatches = (node.title ?? '').toLowerCase().includes(normalized);
        const filteredChildren = (node.children ?? [])
            .map(filterNode)
            .filter(Boolean);
        if (!titleMatches && !filteredChildren.length) {
            return null;
        }
        return {
            ...node,
            children: filteredChildren,
        };
    };

    return tree.map(filterNode).filter(Boolean);
};

/**
 * Collects node ids that should be expanded so all matches (and their paths) are visible.
 * @param {CategoryNode[]} tree
 * @returns {string[]}
 */
export const collectExpandableCategoryNodeIds = (tree = []) => {
    const ids = [];
    const walk = (nodes) => {
        for (const node of nodes) {
            if (node.children?.length) {
                ids.push(String(node.id));
                walk(node.children);
            }
        }
    };
    walk(tree);
    return ids;
};
